/**
 * AbilityCastCapture — auto-screenshot every successful local ability cast
 * (DJ ask 2026-06-12: "auto screenshot with abilities, save to our server,
 * a certain number of frames after, drain hourly, sort by player").
 *
 * Flow: PlayerManager emits PLAYER_ACTION_INTENT {action:'castAbility'} only
 * AFTER all cast gates pass (the same emit that drives remote rendering), so
 * every capture is a real cast — gate rejections never fire it. On each cast
 * we extract world frames from the Pixi stage at the configured offsets
 * (PixiOverlayHost.extractFrameBase64 — ExtractSystem re-render, NOT
 * canvas.toDataURL which is blank on WebGL) and POST each as a raw
 * image/jpeg body to /api/diag/ability-captures/frame with the cast meta in
 * query params. Server side: abilityCaptures.routes.ts stores, lists,
 * serves, and prunes them (default 60-min retention).
 *
 * Gate: ?abilityCapture=1 (auto-injected on /abilitygrounds via
 * AbilityGroundsConfig → coerceModeUrl). Everything is best-effort —
 * failures log and drop, never touch gameplay.
 */
import { EventBus, EVENTS } from '../../shared/EventBus';
import { gameLogger } from '../../utils/GameLogger';
import { PlayerServices } from '../core/Services';
import { PixiOverlayHost } from '../rendering/PixiOverlayHost';
import { entityVfxAnchor } from '../effects/players/entityVfxAnchor';
import { findMobPosition } from '../effects/handlers/entityPositions';
import { computeCaptureFrame, type CaptureFrameRect } from './captureFraming';
import { computeCaptureSchedule } from './captureSchedule';
import { buildCaptureSidecar } from './captureSidecar';
import { VisualLifecycleStore, type VisualEvent } from './VisualLifecycleStore';
import { ChainSegmentStore } from './ChainSegmentStore';
import { TrailStore } from './TrailStore';
import { BeamLinkStore } from './BeamLinkStore';
import { captureRenderStateSnapshot } from '../testhooks/__h2k_render_state';
import { getAbilityData } from '../../shared/content/generated/abilities.gen';
import { publishAbilityCaptureStatus } from './AbilityCaptureStatus';
import {
  ABILITY_CAPTURE_CONFIG,
  getAbilityCaptureFocus,
  isAbilityCaptureEnabled,
} from '../../config/diagnostics/abilityCapture.config';
import { bootPathname } from '../../config/boot/bootUrl';

let installed = false;
let lastCaptureAt = 0;
let activeSequences = 0;
let unsubscribe: (() => void) | null = null;

// Active event-poll teardowns (A1) — cleared by the test-reset seam so a live
// poll can't leak across specs.
const activeEventPolls = new Set<() => void>();

/** Why a capture never fired — de-silences the throttle/concurrency early
 *  returns so a batch run can tell a throttle-drop from an extract/upload
 *  race instead of reading "0 frames" as a dead ability (K12). */
interface CaptureDrop {
  castId: string;
  abilityId: string;
  reason: 'throttle' | 'concurrency';
  deltaMs: number;
  ts: number;
}
const MAX_DROP_BREADCRUMBS = 50;
let captureDrops: CaptureDrop[] = [];

function recordCaptureDrop(drop: CaptureDrop): void {
  captureDrops.push(drop);
  if (captureDrops.length > MAX_DROP_BREADCRUMBS) captureDrops.shift();
}

/** Diagnostic/test seam — recent silent-drop breadcrumbs (throttle / concurrency). */
export function __getAbilityCaptureDrops(): readonly CaptureDrop[] {
  return captureDrops;
}

interface CastIntentPayload {
  action?: string;
  data?: { ability_id?: string; target_id?: string | null };
}

function buildCastMeta(abilityId: string, targetId: string | null | undefined): Record<string, string> {
  const player = PlayerServices.entity() as
    | { id?: string; name?: string; class?: string; position?: { x: number; y: number } }
    | null | undefined;
  const sessionId = (globalThis as { nm?: { getRoom?: () => { sessionId?: string } | null } })
    .nm?.getRoom?.()?.sessionId ?? '';
  const focus = getAbilityCaptureFocus();
  return {
    ability: abilityId,
    player: player?.name ?? player?.id ?? 'unknown',
    cls: player?.class ?? 'unknown',
    sessionId,
    mode: bootPathname(),
    x: String(Math.round(player?.position?.x ?? 0)),
    y: String(Math.round(player?.position?.y ?? 0)),
    targetId: targetId ?? '',
    focus: focus.mode,
    focusSize: String(focus.sizePx),
  };
}

/**
 * Crop rect for one frame of the burst — recomputed PER FRAME because the
 * camera tracks the caster and the cast's interesting pixels (projectile,
 * impact on the target) move between offsets. Falls back to the full
 * viewport (undefined) whenever a piece is missing: pre-init host, no
 * camera source yet, or no resolvable caster.
 */
function resolveCaptureFrame(targetId: string | null | undefined): CaptureFrameRect | undefined {
  try {
    const focus = getAbilityCaptureFocus();
    if (focus.mode === 'viewport') return undefined;
    const screen = PixiOverlayHost.getScreenSize?.();
    if (!screen) return undefined;

    const playerEntity = PlayerServices.entity() as
      | { position?: { x: number; y: number }; width?: number; height?: number }
      | null
      | undefined;
    const playerWorld = entityVfxAnchor(playerEntity);
    const player = playerWorld ? PixiOverlayHost.worldToScreen(playerWorld.x, playerWorld.y) : null;
    if (!player) return undefined;

    let target: { x: number; y: number } | null = null;
    if (focus.mode === 'cast' && targetId) {
      const targetWorld = findMobPosition(targetId);
      if (targetWorld) target = PixiOverlayHost.worldToScreen(targetWorld.x, targetWorld.y);
    }

    return computeCaptureFrame({
      mode: focus.mode,
      sizePx: focus.sizePx,
      screenW: screen.width,
      screenH: screen.height,
      player,
      target,
    });
  } catch {
    // Framing must never cost us the frame — fall back to the viewport.
    return undefined;
  }
}

/**
 * Render-truth sidecar — the frame's JSON twin (what the renderer says it
 * drew). Small body, so `keepalive: true` lets a burst's last sidecars
 * survive a tab close that would kill a pending JPEG. Best-effort: a
 * sidecar failure never costs the frame.
 */
function uploadSidecar(
  castId: string,
  offsetMs: number,
  ability: string,
  targetId: string | null | undefined,
  castStartTs: number,
): Promise<void> {
  const sidecar = buildCaptureSidecar({
    castId,
    offsetMs,
    ability,
    targetId: targetId ?? null,
    castStartTs,
    renderState: captureRenderStateSnapshot(),
    lifecycle: VisualLifecycleStore.all(),
    targetFallback: targetId ? findMobPosition(targetId) : null,
    chainSegments: ChainSegmentStore.all(),
    trailSamples: TrailStore.all(),
    beamSamples: BeamLinkStore.all(),
  });
  const qs = new URLSearchParams({ castId, offsetMs: String(offsetMs) });
  return fetch(`/api/diag/ability-captures/sidecar?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sidecar),
    credentials: 'omit',
    keepalive: true,
  }).then(() => undefined);
}

async function uploadFrame(castId: string, offsetMs: number, meta: Record<string, string>, dataUrl: string): Promise<void> {
  // dataUrl → raw JPEG bytes. Raw body (image/jpeg) deliberately dodges the
  // global express.json() 100kb body cap — the server route uses express.raw.
  const blob = await (await fetch(dataUrl)).blob();
  const qs = new URLSearchParams({ castId, offsetMs: String(offsetMs), ...meta });
  const response = await fetch(`/api/diag/ability-captures/frame?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
    credentials: 'omit',
  });
  if (!response.ok) throw new Error(`frame upload returned ${response.status}`);
}

async function uploadCompletion(
  castId: string,
  payload: {
    scheduledOffsets: readonly number[];
    expectedFrames: number;
    uploadedFrames: number;
    failedFrames: number;
    eventFrames: number;
    startedAt: number;
    completedAt: number;
    runtimeMs: number;
  },
): Promise<void> {
  const response = await fetch(`/api/diag/ability-captures/${encodeURIComponent(castId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'omit',
    keepalive: true,
  });
  if (!response.ok) throw new Error(`completion upload returned ${response.status}`);
}

/**
 * Per-cast frame schedule: derive offsets from the ability's own timeline
 * (windup / projectile flight to the LIVE target distance / duration) so
 * the burst spans windup → release → flight → impact → aftermath instead
 * of clustering inside a long cast bar (the fireball-1000ms-windup lesson,
 * 2026-06-12). Falls back to the fixed config offsets on any failure.
 */
function resolveCaptureSchedule(
  abilityId: string,
  targetId: string | null | undefined,
): { offsets: readonly number[]; castMs: number; impactMs: number } {
  try {
    const data = getAbilityData(abilityId);
    if (!data) return { offsets: ABILITY_CAPTURE_CONFIG.frameOffsetsMs, castMs: 0, impactMs: 0 };

    let distancePx: number | null = null;
    const playerWorld = entityVfxAnchor(
      PlayerServices.entity() as { position?: { x: number; y: number }; width?: number; height?: number } | null,
    );
    const targetWorld = targetId ? findMobPosition(targetId) : null;
    if (playerWorld && targetWorld) {
      distancePx = Math.hypot(targetWorld.x - playerWorld.x, targetWorld.y - playerWorld.y);
    }

    const isChanneled =
      data.mechanicType === 'channel' ||
      !!data.tags?.includes('channel') ||
      !!data.tags?.includes('channeled');

    return computeCaptureSchedule({
      windUpMs: data.windUpMs,
      durationMs: data.durationMs,
      projectileSpeed: data.vfx?.projectileSpeed,
      projectileLifespan: data.vfx?.projectileLifespan,
      hasProjectile: !!data.vfx?.projectileSpeed || !!data.tags?.includes('projectile'),
      distancePx,
      isChanneled,
    });
  } catch {
    return { offsets: ABILITY_CAPTURE_CONFIG.frameOffsetsMs, castMs: 0, impactMs: 0 };
  }
}

/**
 * Photograph ONE frame: sidecar (JSON render-truth) + extracted JPEG, both
 * best-effort. Shared by the a-priori scheduled path and the event-triggered
 * late-frame path (A1) so a beat frame is byte-for-byte the same shape as a
 * scheduled one. `offsetMs` is the cast-relative LABEL (server storage key);
 * the caller controls WHEN this runs.
 */
function captureOneFrame(
  castId: string,
  offsetMs: number,
  meta: Record<string, string>,
  abilityId: string,
  targetId: string | null | undefined,
  castStartTs: number,
): Promise<boolean> {
  const cfg = ABILITY_CAPTURE_CONFIG;
  // Sidecar snapshot fires in the same tick as the JPEG extraction (the JSON
  // must describe the moment the pixels show) but its upload doesn't block the
  // extraction — and `keepalive` lets it survive a tab close.
  const sidecar = uploadSidecar(castId, offsetMs, abilityId, targetId, castStartTs).catch((e) => {
    gameLogger.warn('network', `[AbilityCastCapture] sidecar +${offsetMs}ms failed: ${(e as Error)?.message ?? e}`);
  });
  return (async () => {
    try {
      const frame = resolveCaptureFrame(targetId);
      const dataUrl = await PixiOverlayHost.extractFrameBase64({
        quality: cfg.jpegQuality,
        resolution: frame ? cfg.focusResolution : cfg.resolution,
        frame,
      });
      if (!dataUrl) return false;
      await uploadFrame(castId, offsetMs, meta, dataUrl);
      return true;
    } catch (e) {
      gameLogger.warn('network', `[AbilityCastCapture] frame +${offsetMs}ms failed: ${(e as Error)?.message ?? e}`);
      return false;
    }
  })().finally(() => sidecar);
}

/** True when a lifecycle beat belongs to this cast. Prefers the explicit
 *  abilityId; falls back to targetId; when the beat carries neither (some kill
 *  beats), accepts on recency — the cast-window watermark already bounds it and
 *  the audit casts sequentially. */
function matchesCast(e: VisualEvent, abilityId: string, targetId: string | null | undefined): boolean {
  if (e.abilityId) return e.abilityId === abilityId;
  if (targetId && e.targetId) return e.targetId === targetId;
  return true;
}

/** True when `offsetMs` is within `minGap` of any already-scheduled offset —
 *  keeps an event frame from colliding with a scheduled `${offset}ms.jpg`. */
function nearAnyOffset(offsetMs: number, offsets: Set<number>, minGap: number): boolean {
  for (const o of offsets) {
    if (Math.abs(o - offsetMs) < minGap) return true;
  }
  return false;
}

function captureCast(abilityId: string, targetId: string | null | undefined): void {
  const cfg = ABILITY_CAPTURE_CONFIG;
  const now = Date.now();
  // castId is the storage key — server validates [A-Za-z0-9_-]+ so keep it clean.
  const castId = `${now}_${abilityId}`.replace(/[^A-Za-z0-9_-]/g, '_');
  const sinceLast = now - lastCaptureAt;

  // De-silenced drop paths (K12): a silent early-return made a starved capture
  // read as a dead ability. Log + a breadcrumb so a later run disambiguates a
  // throttle-drop from an extract/upload race. Behavior (drop) is unchanged.
  if (sinceLast < cfg.minIntervalMs) {
    recordCaptureDrop({ castId, abilityId, reason: 'throttle', deltaMs: sinceLast, ts: now });
    publishAbilityCaptureStatus({
      castId, abilityId, targetId: targetId ?? null, state: 'dropped', startedAt: now,
      scheduledOffsets: [], expectedFrames: 0, uploadedFrames: 0, failedFrames: 0,
      eventFrames: 0, dropReason: 'throttle',
    });
    gameLogger.debug('network', `[AbilityCastCapture] throttle-drop ${abilityId} (Δ${sinceLast}ms < minIntervalMs ${cfg.minIntervalMs}) — NOT an ability failure`);
    return;
  }
  if (activeSequences >= cfg.maxConcurrentSequences) {
    recordCaptureDrop({ castId, abilityId, reason: 'concurrency', deltaMs: sinceLast, ts: now });
    publishAbilityCaptureStatus({
      castId, abilityId, targetId: targetId ?? null, state: 'dropped', startedAt: now,
      scheduledOffsets: [], expectedFrames: 0, uploadedFrames: 0, failedFrames: 0,
      eventFrames: 0, dropReason: 'concurrency',
    });
    gameLogger.debug('network', `[AbilityCastCapture] concurrency-drop ${abilityId} (${activeSequences} active ≥ maxConcurrentSequences ${cfg.maxConcurrentSequences}) — NOT an ability failure`);
    return;
  }
  lastCaptureAt = now;
  activeSequences += 1;

  const schedule = resolveCaptureSchedule(abilityId, targetId);
  const meta = {
    ...buildCastMeta(abilityId, targetId),
    castMs: String(schedule.castMs),
    impactMs: String(schedule.impactMs),
    scheduledOffsets: schedule.offsets.join(','),
    expectedFrames: String(schedule.offsets.length),
  };

  let uploadedFrames = 0;
  let failedFrames = 0;
  publishAbilityCaptureStatus({
    castId,
    abilityId,
    targetId: targetId ?? null,
    state: 'started',
    startedAt: now,
    scheduledOffsets: [...schedule.offsets],
    expectedFrames: schedule.offsets.length,
    uploadedFrames,
    failedFrames,
    eventFrames: 0,
  });

  const frames = schedule.offsets.map(
    (offsetMs) =>
      new Promise<boolean>((resolve) => {
        window.setTimeout(() => {
          void captureOneFrame(castId, offsetMs, meta, abilityId, targetId, now).then((ok) => {
            if (ok) uploadedFrames += 1;
            else failedFrames += 1;
            resolve(ok);
          });
        }, offsetMs);
      }),
  );

  // --- Event-triggered late frames (A1 / VQA-08) ---------------------------
  // The a-priori schedule front-loads windup→impact but can't predict a beat
  // that fires on a runtime timer (ravager whirl) or a server-confirmed kill
  // (/pve dissolve @ +2.6s). Poll VisualLifecycleStore.all() against a
  // watermark and photograph the beat when it actually fires. Event offsets are
  // deduped against scheduled ones (minGapMs) so channel body frames (A9) and
  // event beats never double-sample the same moment.
  const scheduledOffsets = new Set<number>(schedule.offsets);
  const seenBeatIds = new Set<string>();
  let eventFrames = 0;
  const eventFramePromises: Array<Promise<boolean>> = [];

  const scanBeats = (): void => {
    const events = VisualLifecycleStore.all();
    const from = Math.max(0, events.length - cfg.schedule.eventScanN);
    for (let i = from; i < events.length; i++) {
      const e = events[i];
      if (seenBeatIds.has(e.id)) continue;
      seenBeatIds.add(e.id);
      if (e.timestamp < now) continue; // watermark: only beats after cast start
      if (!(cfg.schedule.eventBeatTypes as readonly string[]).includes(e.type)) continue;
      if (!matchesCast(e, abilityId, targetId)) continue;
      if (eventFrames >= cfg.schedule.maxEventFrames) continue;
      // Label the frame by the beat's own offset from cast; capture eventPadMs
      // after observing it (the beat just fired — grab it now, not at label).
      const labelOffsetMs = Math.round(e.timestamp - now) + cfg.schedule.eventPadMs;
      if (labelOffsetMs > cfg.schedule.eventMaxMs) continue;
      if (nearAnyOffset(labelOffsetMs, scheduledOffsets, cfg.schedule.minGapMs)) continue;
      eventFrames += 1;
      scheduledOffsets.add(labelOffsetMs);
      eventFramePromises.push(
        new Promise<boolean>((resolve) => {
          window.setTimeout(() => {
            void captureOneFrame(castId, labelOffsetMs, meta, abilityId, targetId, now).then((ok) => {
              if (ok) uploadedFrames += 1;
              else failedFrames += 1;
              resolve(ok);
            });
          }, cfg.schedule.eventPadMs);
        }),
      );
    }
  };

  let pollTimer: number | null = null;
  const teardown = (): void => {
    if (pollTimer !== null) {
      window.clearTimeout(pollTimer);
      pollTimer = null;
    }
    activeEventPolls.delete(teardown);
  };
  const poll = (): void => {
    scanBeats();
    if (Date.now() - now < cfg.schedule.eventMaxMs && eventFrames < cfg.schedule.maxEventFrames) {
      pollTimer = window.setTimeout(poll, cfg.schedule.minGapMs);
    } else {
      teardown();
    }
  };
  activeEventPolls.add(teardown);
  pollTimer = window.setTimeout(poll, cfg.schedule.minGapMs);

  // The concurrency slot is held ONLY for the a-priori scheduled frames — the
  // event window is additive and must NOT extend the slot, else a same-page
  // batch would concurrency-drop more casts (throttle behavior stays unchanged).
  void Promise.all(frames).finally(() => {
    activeSequences -= 1;
    gameLogger.debug(
      'network',
      `[AbilityCastCapture] ${castId} uploaded (${schedule.offsets.length} frames @ ${schedule.offsets.join('/')}ms)`,
    );
  });

  // Close the event window independently: tear the poll down and log the late
  // frames it caught (best-effort, no concurrency slot held).
  window.setTimeout(() => {
    teardown();
    void Promise.allSettled([...frames, ...eventFramePromises]).then(async () => {
      if (eventFrames > 0) {
        gameLogger.debug('network', `[AbilityCastCapture] ${castId} + ${eventFrames} event-triggered late frame(s)`);
      }
      const completedAt = Date.now();
      const runtimeMs = completedAt - now;
      const receipt = {
        scheduledOffsets: schedule.offsets,
        expectedFrames: schedule.offsets.length + eventFrames,
        uploadedFrames,
        failedFrames,
        eventFrames,
        startedAt: now,
        completedAt,
        runtimeMs,
      };
      try {
        await uploadCompletion(castId, receipt);
      } catch (e) {
        gameLogger.warn('network', `[AbilityCastCapture] completion receipt failed: ${(e as Error)?.message ?? e}`);
      }
      publishAbilityCaptureStatus({
        castId,
        abilityId,
        targetId: targetId ?? null,
        state: 'complete',
        startedAt: now,
        completedAt,
        runtimeMs,
        scheduledOffsets: [...schedule.offsets],
        expectedFrames: receipt.expectedFrames,
        uploadedFrames,
        failedFrames,
        eventFrames,
      });
    });
  }, cfg.schedule.eventMaxMs + cfg.schedule.eventPadMs);
}

export function installAbilityCastCapture(): void {
  if (installed) return;
  if (!isAbilityCaptureEnabled()) return;
  installed = true;

  unsubscribe = EventBus.getInstance().on(EVENTS.PLAYER_ACTION_INTENT, (payload: CastIntentPayload) => {
    if (payload?.action !== 'castAbility') return;
    const abilityId = payload.data?.ability_id;
    if (!abilityId) return;
    try {
      captureCast(abilityId, payload.data?.target_id);
    } catch {
      /* capture is diagnostic-only — never throw into the cast path */
    }
  });
  gameLogger.info('boot', '[AbilityCastCapture] installed — casts auto-capture to /api/diag/ability-captures (?abilityCapture=1)');
}

/** Test seam — detach the listener + reset module state between specs. */
export function __resetAbilityCastCaptureForTests(): void {
  unsubscribe?.();
  unsubscribe = null;
  installed = false;
  lastCaptureAt = 0;
  activeSequences = 0;
  captureDrops = [];
  for (const t of Array.from(activeEventPolls)) t();
  activeEventPolls.clear();
}
